import React, { useEffect, useState } from 'react';
import { Target, Plus, Loader2, AlertTriangle } from 'lucide-react';
import type {
  TaskListResponse,
  TaskStatus,
  GoalTemplateDetail,
  PatientGoalDetail,
  VisitTypeId,
} from '../../services/api/types.gen';
import { canAddGoals } from '../../constants/visitTypes';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
} from '@roger/ui-components';
import {
  getGoalTemplatesForCreation,
  getPatientGoalsForVisit,
  client,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import AddGoalsBox from './AddGoalsBox';
import CreateGoalFromTemplateCard from './CreateGoalFromTemplateCard';
import DisplayGoalCard from './DisplayGoalCard';

const EDITABLE_VISIT_STATUSES = new Set<TaskStatus>([
  'not_started',
  'saved',
  'pending',
  'scheduled',
  'in_progress',
]);

interface GoalsTabProps {
  open: boolean;
  onClose: () => void;
  patientFirstName: string;
  patientLastName: string;
  visit: TaskListResponse;
  inline?: boolean;
}

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
    <Target className='h-12 w-12 mb-4 opacity-30' />
    <p className='text-base font-medium'>{message}</p>
  </div>
);

const GoalsTab: React.FC<GoalsTabProps> = ({
  open,
  onClose,
  patientFirstName,
  patientLastName,
  visit,
  inline = false,
}) => {
  const [isAddGoalsOpen, setIsAddGoalsOpen] = useState(false);
  const [freeTextGoalKeys, setFreeTextGoalKeys] = useState<string[]>([]);
  const [templatesFromCreation, setTemplatesFromCreation] = useState<
    GoalTemplateDetail[]
  >([]);
  const [activeGoals, setActiveGoals] = useState<PatientGoalDetail[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'met' | 'unattainable'>(
    'active'
  );
  const [isLoading, setIsLoading] = useState(false);
  const { error: showError } = useToast();

  // Resets all component state when the Goals tab dialog is closed. IMPORTANT:
  // When adding new state variables, add them to this function so the component
  // starts fresh each time it's opened.
  const resetGoalsTabState = () => {
    setIsAddGoalsOpen(false);
    setFreeTextGoalKeys([]);
    setTemplatesFromCreation([]);
    setActiveGoals([]);
    setActiveTab('active');
    setIsLoading(false);
  };

  useEffect(() => {
    if (!open) {
      resetGoalsTabState();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      client,
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
      })
      .catch(() => {
        showError(
          'Error loading goals',
          'There was an error loading goals for this visit.'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, visit.id]);

  const handleAddGoals = (goalTemplateIds: string[]) => {
    if (goalTemplateIds.length === 0) return;
    getGoalTemplatesForCreation({
      path: { visit_id: visit.id },
      body: goalTemplateIds,
      client,
      throwOnError: true,
    })
      .then(response => {
        const newTemplates = response.data ?? [];
        setTemplatesFromCreation(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          return [...prev, ...newTemplates.filter(t => !existingIds.has(t.id))];
        });
      })
      .catch(() => {
        showError(
          'Error selecting templates',
          'There was an error selecting those templates.'
        );
      });
  };

  const handleGoalDiscarded = (templateId: string) => {
    setTemplatesFromCreation(prev => prev.filter(t => t.id !== templateId));
  };

  const handleGoalSaved = (templateId: string) => {
    setTemplatesFromCreation(prev => prev.filter(t => t.id !== templateId));
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      client,
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load goals');
      });
  };

  const handleGoalProgressSaved = (updatedGoals: PatientGoalDetail[]) => {
    setActiveGoals(updatedGoals);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'active' | 'met' | 'unattainable');
  };

  const handleOpenAddGoals = () => {
    setIsAddGoalsOpen(true);
  };

  const handleCloseAddGoals = () => {
    setIsAddGoalsOpen(false);
  };

  const handleUseFreeText = () => {
    setFreeTextGoalKeys(prev => [...prev, `free-text-${Date.now()}`]);
  };

  const handleCancelFreeTextGoal = (key: string) => {
    setFreeTextGoalKeys(prev => prev.filter(k => k !== key));
  };

  const handleFreeTextGoalSaved = (key: string) => {
    setFreeTextGoalKeys(prev => prev.filter(k => k !== key));
    getPatientGoalsForVisit({
      path: { visit_id: visit.id },
      client,
      throwOnError: true,
    })
      .then(response => {
        setActiveGoals(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load goals');
      });
  };

  const handleSaveForTemplate = (templateId: string) => () => {
    handleGoalSaved(templateId);
  };

  const handleCancelForTemplate = (templateId: string) => () => {
    handleGoalDiscarded(templateId);
  };

  const isValidStateForEditing = EDITABLE_VISIT_STATUSES.has(visit.status);
  const isGoalEligible = canAddGoals(visit.visit_type as VisitTypeId);

  const metGoals = activeGoals.filter(goal => goal.is_met ?? false);
  const unattainableGoals = activeGoals.filter(
    goal => goal.is_unattainable ?? false
  );
  const activeGoalsFiltered = activeGoals.filter(
    goal => !(goal.is_met ?? false) && !(goal.is_unattainable ?? false)
  );

  const start = visit.episode_start_date;
  const end = visit.episode_end_date;
  const hasEpisodeDates = start && end;
  const showCreationCards =
    (templatesFromCreation.length > 0 || freeTextGoalKeys.length > 0) &&
    hasEpisodeDates;
  const showEpisodeDateError =
    (templatesFromCreation.length > 0 || freeTextGoalKeys.length > 0) &&
    !hasEpisodeDates;

  const content = (
    <div className={inline ? 'flex flex-col h-full min-h-[500px]' : 'contents'}>
      {!inline && (
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Target className='h-5 w-5' />
            Goals for {patientFirstName} {patientLastName}
          </DialogTitle>
          <DialogDescription>
            Patient care goals and objectives
          </DialogDescription>
        </DialogHeader>
      )}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex flex-col flex-1 min-h-0'
      >
        <div className='mb-3 flex flex-wrap items-center gap-2'>
          <TabsList className='h-9 w-fit rounded-lg bg-muted/40 p-1'>
            <TabsTrigger value='active' className='h-7 px-3 text-sm'>
              Active ({activeGoalsFiltered.length})
            </TabsTrigger>
            <TabsTrigger value='met' className='h-7 px-3 text-sm'>
              Met ({metGoals.length})
            </TabsTrigger>
            <TabsTrigger value='unattainable' className='h-7 px-3 text-sm'>
              Unattainable ({unattainableGoals.length})
            </TabsTrigger>
          </TabsList>
          {!isValidStateForEditing && (
            <div className='inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
              <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
              Editing goals is unavailable for this visit.
            </div>
          )}
          {isValidStateForEditing &&
            activeTab === 'active' &&
            isGoalEligible && (
              <Button
                size='sm'
                onClick={handleOpenAddGoals}
                className='flex items-center gap-1'
              >
                <Plus className='h-4 w-4' />
                Add Goals
              </Button>
            )}
        </div>
        <TabsContent
          value='active'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : showEpisodeDateError ? (
              <EmptyState message='Episode dates are required to add goals. Please refresh the page.' />
            ) : (
              <div className='space-y-2'>
                {showCreationCards && (
                  <div className='space-y-2 rounded-md border border-primary/20 bg-primary/5 p-2'>
                    <p className='text-xs font-medium text-primary'>
                      Goals currently being added
                    </p>
                    <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                      {freeTextGoalKeys.map(key => (
                        <CreateGoalFromTemplateCard
                          key={key}
                          template={null}
                          episodeStartDate={start}
                          episodeEndDate={end}
                          visitId={visit.id}
                          onSave={() => handleFreeTextGoalSaved(key)}
                          onCancel={() => handleCancelFreeTextGoal(key)}
                        />
                      ))}
                      {templatesFromCreation.map(template => (
                        <CreateGoalFromTemplateCard
                          key={template.id}
                          template={template}
                          episodeStartDate={start}
                          episodeEndDate={end}
                          visitId={visit.id}
                          onSave={handleSaveForTemplate(template.id)}
                          onCancel={handleCancelForTemplate(template.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {showCreationCards && activeGoalsFiltered.length > 0 && (
                  <div className='relative flex items-center py-1'>
                    <div className='flex-grow border-t border-border' />
                    <span className='mx-3 text-xs text-muted-foreground whitespace-nowrap'>
                      Existing goals
                    </span>
                    <div className='flex-grow border-t border-border' />
                  </div>
                )}
                {activeGoalsFiltered.length > 0 ? (
                  <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                    {activeGoalsFiltered.map(goal => (
                      <DisplayGoalCard
                        key={goal.id}
                        goal={goal}
                        visitId={visit.id}
                        isViewOnly={!isValidStateForEditing}
                        episodeStartDate={visit.episode_start_date}
                        episodeEndDate={visit.episode_end_date}
                        onGoalProgressSaved={handleGoalProgressSaved}
                      />
                    ))}
                  </div>
                ) : (
                  !showCreationCards && <EmptyState message='No goals yet' />
                )}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent
          value='met'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : metGoals.length > 0 ? (
              <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                {metGoals.map(goal => (
                  <DisplayGoalCard
                    key={goal.id}
                    goal={goal}
                    visitId={visit.id}
                    isViewOnly={true}
                    episodeStartDate={visit.episode_start_date}
                    episodeEndDate={visit.episode_end_date}
                    onGoalProgressSaved={handleGoalProgressSaved}
                  />
                ))}
              </div>
            ) : (
              <EmptyState message='No met goals for this patient' />
            )}
          </div>
        </TabsContent>
        <TabsContent
          value='unattainable'
          className='data-[state=active]:flex flex-col data-[state=active]:flex-1 min-h-0'
        >
          <div className='overflow-y-auto flex-1 pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12 text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : unattainableGoals.length > 0 ? (
              <div className='grid grid-cols-1 xl:grid-cols-2 items-start gap-2'>
                {unattainableGoals.map(goal => (
                  <DisplayGoalCard
                    key={goal.id}
                    goal={goal}
                    visitId={visit.id}
                    isViewOnly={true}
                    episodeStartDate={visit.episode_start_date}
                    episodeEndDate={visit.episode_end_date}
                    onGoalProgressSaved={handleGoalProgressSaved}
                  />
                ))}
              </div>
            ) : (
              <EmptyState message='No unattainable goals for this patient' />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  if (inline) {
    return (
      <>
        {content}
        <AddGoalsBox
          open={isAddGoalsOpen}
          onClose={handleCloseAddGoals}
          visitId={visit.id}
          onAddGoals={handleAddGoals}
          onUseFreeText={handleUseFreeText}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className='max-w-lg h-[75vh] flex flex-col overflow-hidden'>
          {content}
        </DialogContent>
      </Dialog>
      <AddGoalsBox
        open={isAddGoalsOpen}
        onClose={handleCloseAddGoals}
        visitId={visit.id}
        onAddGoals={handleAddGoals}
        onUseFreeText={handleUseFreeText}
      />
    </>
  );
};

export default GoalsTab;
